// A number field that can be typed through values it will not commit.
//
// A controlled React input is written back to its `value` after every keystroke
// that does not change it. A field that refuses a value on the way to a valid one
// (the "2" of "250", a lone "-", a number that would meet its neighbour) was
// therefore reset on each such keystroke and could not be typed into at all. The
// text being typed is held here instead, committed the moment it is valid, and
// dropped on blur so the field shows what the model holds.
//
// Committing as soon as a value is valid means a valid prefix is committed on
// the way to an invalid whole: typing 250 into a field capped at 29 commits 25.
// So leaving the field on a draft it refuses puts back the value it had when
// editing began, rather than keeping a number nobody asked for.
//
// The range is enforced here and never handed to the browser. `min`, `max` and a
// whole-number `step` on the element make it :invalid, which main.scss paints
// pink app-wide: a stored 13.7 under step 1 opened pink, and a refused value
// turned pink with nothing saying why. The caller says why, off
// `onRefusedChange`.
import React from 'react';

/** Whether a typed value may be committed: a number, inside its range. */
export function accepts(
  value: number,
  range: { min?: number; max?: number }
): boolean {
  if (!Number.isFinite(value)) return false;
  if (range.min !== undefined && value < range.min) return false;
  if (range.max !== undefined && value > range.max) return false;
  return true;
}

/**
 * The value to put back when a field is left, or null to leave it be: the one it
 * had when editing began, if what is left in it would not be committed and a
 * prefix on the way was.
 */
export function restoreOnBlur(
  draft: string | null,
  before: number | null | undefined,
  value: number | null | undefined,
  range: { min?: number; max?: number }
): number | null {
  if (draft === null) return null;
  if (accepts(draft === '' ? NaN : Number(draft), range)) return null;
  if (typeof before !== 'number' || before === value) return null;
  return before;
}

export default function DraftNumberInput({
  value,
  onCommit,
  onRefusedChange,
  min,
  max,
  ...input
}: Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange' | 'type' | 'min' | 'max'
> & {
  value: number | null | undefined;
  onCommit: (value: number) => void;
  /** Called when the field starts or stops holding back what is typed. */
  onRefusedChange?: (refused: boolean) => void;
  /** Inclusive. A value outside is kept on screen while typed, never committed. */
  min?: number;
  max?: number;
}) {
  const [draft, setDraft] = React.useState<string | null>(null);
  const before = React.useRef<number | null | undefined>(value);
  const refused =
    draft !== null && draft !== '' && !accepts(Number(draft), { min, max });
  // Read through a ref, not listed as a dependency: a caller passing an inline
  // arrow would make the effect fire on every parent render, and one that sets
  // state in it would render again, forever.
  const onRefusedChangeRef = React.useRef(onRefusedChange);
  onRefusedChangeRef.current = onRefusedChange;
  React.useEffect(() => {
    onRefusedChangeRef.current?.(refused);
  }, [refused]);
  return (
    <input
      {...input}
      type="number"
      step="any"
      value={draft ?? value ?? ''}
      onFocus={(event) => {
        before.current = value;
        input.onFocus?.(event);
      }}
      onChange={(event) => {
        setDraft(event.target.value);
        const next = event.target.valueAsNumber;
        if (accepts(next, { min, max })) onCommit(next);
      }}
      onBlur={(event) => {
        const restore = restoreOnBlur(draft, before.current, value, {
          min,
          max,
        });
        if (restore !== null) onCommit(restore);
        setDraft(null);
        input.onBlur?.(event);
      }}
    />
  );
}
